import { z } from 'zod';

import { ReportColumn, ReportSortBy, ReportTransactionRole } from './common';
import { GuestReportBookingSourceFilter } from './guest-report';
import { ReportFormat } from './report';
import { DateStringSchema } from '../../common';
import { buildQueryString } from '../../utils/querystring';
import { makeEndpoint, paginateResponseSchema, stringBooleanSchema } from '../common';

export const ExperienceReportStaticColumns01 = [
  ReportColumn.EXPERIENCE_DATE,
  ReportColumn.START_TIME,
  ReportColumn.END_TIME,
  ReportColumn.DURATION,
  ReportColumn.TRANSACTION_DATE,
  ReportColumn.FIRST_NAME,
  ReportColumn.LAST_NAME,
  ReportColumn.EMAIL,
  ReportColumn.CONFIRMATION_CODE,
  ReportColumn.EXPERIENCE_NAME,
  ReportColumn.TOTAL_PARTICIPANTS,
  ReportColumn.TRANSACTION_ROLE,
  ReportColumn.PARTNER_NAME,
] as const satisfies ReadonlyArray<ReportColumn>;

export const ExperienceReportStaticColumns02 = [
  ReportColumn.TOTAL,
  ReportColumn.TOTAL_PAID,
  ReportColumn.DISCOUNT_AMOUNT,
  ReportColumn.REFUNDS,
  ReportColumn.PAYMENT_METHOD,
  ReportColumn.BOOKING_STATUS,
  ReportColumn.CHECK_IN_STATUS,
  ReportColumn.CATEGORY,
  ReportColumn.GUEST_NOTES,
  ReportColumn.INTERNAL_NOTES,
] as const satisfies ReadonlyArray<ReportColumn>;

export const ExperienceReportColumn = [
  ...ExperienceReportStaticColumns01,
  ReportColumn.PRICE_TIERS,
  ReportColumn.ADD_ONS,
  ...ExperienceReportStaticColumns02,
  ReportColumn.CUSTOM_QUESTIONS,
] as const satisfies ReadonlyArray<ReportColumn>;

export type ExperienceReportColumnType = (typeof ExperienceReportColumn)[number];

export const experienceColumnsMap = {
  [ReportColumn.EXPERIENCE_DATE]: 'Experience Date',
  [ReportColumn.START_TIME]: 'Start Time',
  [ReportColumn.END_TIME]: 'End Time',
  [ReportColumn.DURATION]: 'Duration',
  [ReportColumn.TRANSACTION_DATE]: 'Transaction Date UTC',
  [ReportColumn.FIRST_NAME]: 'First Name',
  [ReportColumn.LAST_NAME]: 'Last Name',
  [ReportColumn.EMAIL]: 'Email',
  [ReportColumn.CONFIRMATION_CODE]: 'Confirmation Code',
  [ReportColumn.EXPERIENCE_NAME]: 'Experience Name',
  [ReportColumn.TOTAL_PARTICIPANTS]: 'Total Participants',
  [ReportColumn.TRANSACTION_ROLE]: 'Transaction Role',
  [ReportColumn.PARTNER_NAME]: 'Partner Name',
  // PRICE TIERS
  // ADD ONS
  [ReportColumn.TOTAL]: 'Total Price',
  [ReportColumn.TOTAL_PAID]: 'Total Paid',
  [ReportColumn.DISCOUNT_AMOUNT]: 'Discount Amount',
  [ReportColumn.REFUNDS]: 'Refunds',
  [ReportColumn.PAYMENT_METHOD]: 'Payment Method',
  [ReportColumn.BOOKING_STATUS]: 'Booking Status',
  [ReportColumn.CHECK_IN_STATUS]: 'Check-In Status',
  [ReportColumn.CATEGORY]: 'Category',
  [ReportColumn.GUEST_NOTES]: 'Guest Notes',
  [ReportColumn.INTERNAL_NOTES]: 'Internal Notes',
  // CUSTOM QUESTIONS
} as const satisfies Record<
  Exclude<ExperienceReportColumnType, ReportColumn.CUSTOM_QUESTIONS | ReportColumn.PRICE_TIERS | ReportColumn.ADD_ONS>,
  string
>;

export const ExperienceReportSortBy = [
  ReportSortBy.START_DATE_TIME,
  ReportSortBy.TRANSACTION_DATE,
] as const satisfies ReadonlyArray<ReportSortBy>;

const baseExperienceReportFilters = {
  from: z.union([DateStringSchema, z.string().datetime()]).optional(),
  to: z.union([DateStringSchema, z.string().datetime()]).optional(),
  showNegativeDiscountValues: stringBooleanSchema.optional(),
  confirmationCode: z.string().optional(),
  cartId: z.string().optional(),
  experienceIds: z.array(z.string().uuid()).optional(),
  collectionIds: z.array(z.string().uuid()).optional(),
  categoryIds: z.array(z.string().uuid()).optional(),
  bookingSources: z.array(z.nativeEnum(GuestReportBookingSourceFilter)).optional(),
  email: z.string().optional(),
  lastName: z.string().optional(),
  page: z.coerce.number().optional(),
  limit: z.coerce.number().optional(),
};

export type BaseExperienceReportFiltersKeys = keyof typeof baseExperienceReportFilters;

const experienceReportFiltersSchema = z.object(baseExperienceReportFilters);

export type ExperienceReportFilters = z.infer<typeof experienceReportFiltersSchema>;

const BaseExperienceReportRequestSchema = z.object({
  columns: z.array(z.enum(ExperienceReportColumn)),
  params: experienceReportFiltersSchema,
  sortBy: z.enum(ExperienceReportSortBy),
  sortDir: z.enum(['ASC', 'DESC']).optional(),
  format: z.enum([ReportFormat.CSV, ReportFormat.PDF]).optional(),
});
export const BrandExperienceReportRequestSchema = BaseExperienceReportRequestSchema.extend({
  brandId: z.string().uuid().optional(),
});

export const ExperienceReportRequestSchema = BrandExperienceReportRequestSchema;
export type ExperienceReportRequest = z.infer<typeof ExperienceReportRequestSchema>;

export const ExperienceReportWithHeaderSchema = z.object({
  'Experience Date': z.string().optional(),
  'Start Time': z.string().optional(),
  'End Time': z.string().optional(),
  Duration: z.string().optional(),
  'Transaction Date UTC': z.string().optional(),
  'First Name': z.string().optional(),
  'Last Name': z.string().optional(),
  Email: z.string().optional(),
  'Confirmation Code': z.string().optional(),
  'Experience Name': z.string().optional(),
  'Total Participants': z.string().optional(),
  'Transaction Role': z.string().optional(),
  'Partner Name': z.string().optional(),
  // PRICE TIERS
  // ADD ONS
  'Total Price': z.string().optional(),
  'Total Paid': z.string().optional(),
  'Discount Amount': z.string().optional(),
  Refunds: z.string().optional(),
  'Payment Method': z.string().optional(),
  'Booking Status': z.string().optional(),
  'Check-In Status': z.string().optional(),
  Category: z.string().optional(),
  'Guest Notes': z.string().optional(),
  'Internal Notes': z.string().optional(),
  // CUSTOM QUESTIONS
} satisfies Record<(typeof experienceColumnsMap)[Exclude<keyof typeof experienceColumnsMap, ReportColumn.TAXES>], z.ZodType>);
export type ExperienceReportWithHeader = z.input<typeof ExperienceReportWithHeaderSchema>;

const PreviewExperienceReportRequestSchema = experienceReportFiltersSchema.extend({
  sortBy: z.enum(ExperienceReportSortBy),
});

const experienceReportItemSchema = z.object({
  experienceDate: z.string(),
  startTime: z.string(),
  endTime: z.string(),
  duration: z.string(),
  transactionDate: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  confirmationCode: z.string(),
  experienceName: z.string(),
  totalParticipants: z.number(),
  transactionRole: z.nativeEnum(ReportTransactionRole).or(z.literal('')),
  partnerName: z.string(),
  priceTiers: z.record(z.number().or(z.literal(''))),
  addOns: z.record(z.number().or(z.literal(''))),
  total: z.number(),
  totalPaid: z.number(),
  discountAmount: z.number().or(z.literal('')),
  refunds: z.number().or(z.literal('')),
  paymentMethod: z.string(),
  bookingStatus: z.string(),
  checkInStatus: z.string().nullable(),
  category: z.string().nullable(),
  guestNotes: z.string().nullable(),
  internalNotes: z.string().nullable(),
  customQuestions: z.record(z.string()),
});

export type ExperienceReportItem = z.infer<typeof experienceReportItemSchema>;

export const PreviewExperienceReportEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ query, brandId }: { query: z.input<typeof PreviewExperienceReportRequestSchema>; brandId: string }) =>
    `v1/brands/${brandId}/reports/experiences?${buildQueryString(query)}`,
  responseSchema: paginateResponseSchema(experienceReportItemSchema),
  requestSchema: z.void(),
  querySchema: PreviewExperienceReportRequestSchema,
});
